import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Paper,
  Typography,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import { getToken } from "../../../api/Account";
import { DataContext } from "../../../context/DataContext";

import { isAccess, simpleMessage } from "../../../helpers/Helpers";
import { formatearMonto } from "../../../helpers/formato";

import {
  CargandoTabla,
  ConfirmDialog,
  EstadoError,
  EstadoVacio,
  useAviso,
} from "../../../components/base";

import AddAbono from "./quoteComponents/AddAbono";
import {
  deleteQuoteAsync,
  deleteQuoteNominalAsync,
  getCreditQuotesAsync,
} from "../../../api/Credits";
import { celdaFecha, celdaMonto, encabezadoTabla, fechaDeCredito } from "../comunes";

// Lo que devuelve `errorResponse` cuando el servidor dice que la sesión sigue abierta en otro equipo.
const ERROR_OTRA_SESION = "Sesión activa en otro dispositivo";

const CreditAbono = ({ id, clientName, isNominal, onAbonosCambiaron }) => {
  const [quotesList, setQuotesList] = useState([]);
  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
  const aviso = useAviso();
  const token = getToken();
  const [localReload, setLocalReload] = useState(false);

  // Estado local de la pestaña: esqueleto mientras carga y error con "Reintentar".
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  // Confirmación de anular. Se conserva la fila al cerrar para que el texto no cambie
  // durante la animación de salida.
  const [confirmacion, setConfirmacion] = useState({ abierto: false, fila: null });
  const [procesando, setProcesando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await getCreditQuotesAsync(token, id);
      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        setError(true);
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        return;
      }
      setIsLoading(false);
      setQuotesList(result.data.result);
      setCargando(false);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      setIsLoading(false);
      setCargando(false);
      setError("conexion");
    }
  };

  useEffect(() => {
    cargar();
  }, [localReload]);

  const [addQuoteModal, setAddQuoteModal] = useState(false);

  // Cuando se agrega un abono (AddAbono llama a esta función) también se avisa al encabezado
  // para que descarte el saldo que tenía.
  const recargarAbonos = (valor) => {
    setLocalReload(valor);
    if (onAbonosCambiaron) onAbonosCambiaron();
  };

  const AnulateQuote = async (item) => {
    setIsLoading(true);
    try {
      const result = isNominal
        ? await deleteQuoteNominalAsync(token, item)
        : await deleteQuoteAsync(token, item);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }
      setIsLoading(false);
      setLocalReload(!localReload);
      if (onAbonosCambiaron) onAbonosCambiaron();
      aviso.exito("Abono anulado");
    } catch (e) {
      setIsLoading(false);
      aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    }
  };

  const pedirAnular = (fila) => setConfirmacion({ abierto: true, fila });
  const cerrarConfirmacion = () =>
    setConfirmacion((actual) => ({ ...actual, abierto: false }));

  // Solo aquí se llama a la función de verdad, y una sola vez por confirmación.
  const confirmar = async () => {
    setProcesando(true);
    try {
      await AnulateQuote(confirmacion.fila.id);
    } finally {
      setProcesando(false);
      cerrarConfirmacion();
    }
  };

  const puedeAgregar = isAccess(access, "CREDITS CREATE");
  const puedeAnular = isAccess(access, "CREDITS DELETE");
  const filaAnular = confirmacion.fila;

  const botonAgregar = (
    <Button
      onClick={() => {
        setAddQuoteModal(true);
      }}
      variant="contained"
      startIcon={<AddIcon />}
    >
      Agregar abono
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={6} columnas={6} etiqueta="Cargando los abonos…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los abonos"
        }
        onReintentar={cargar}
      />
    );
  } else if (quotesList.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="Sin abonos"
        descripcion="Este crédito todavía no tiene abonos registrados."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <TableContainer>
        <Table aria-label="Abonos del crédito" size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={encabezadoTabla}>Abono</TableCell>
              <TableCell sx={encabezadoTabla}>Fecha</TableCell>
              <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Capital</TableCell>
              <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                Interés corriente
              </TableCell>
              <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                Total abonado
              </TableCell>
              <TableCell sx={encabezadoTabla}>Concepto</TableCell>
              {puedeAnular ? (
                <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                  Acciones
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {quotesList.map((row) => {
              return (
                <TableRow key={row.id} hover>
                  <TableCell component="th" scope="row" sx={celdaFecha}>
                    {row.id}
                  </TableCell>
                  <TableCell sx={celdaFecha}>{fechaDeCredito(row.fechaPago)}</TableCell>
                  <TableCell sx={celdaMonto}>{formatearMonto(row.ac)}</TableCell>
                  <TableCell sx={celdaMonto}>{formatearMonto(row.ic)}</TableCell>
                  <TableCell sx={celdaMonto}>{formatearMonto(row.totalRecibido)}</TableCell>
                  <TableCell>{row.concepto}</TableCell>
                  {puedeAnular ? (
                    <TableCell sx={{ textAlign: "right" }}>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<BlockIcon />}
                        aria-label={`Anular el abono #${row.id}`}
                        onClick={() => pedirAnular(row)}
                      >
                        Anular
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <div>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent={"space-between"}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Typography
            component="h2"
            variant="h6"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            Abonos
          </Typography>
          {puedeAgregar ? botonAgregar : <></>}
        </Stack>

        {contenido}
      </Paper>

      <ConfirmDialog
        abierto={confirmacion.abierto}
        peligro
        cargando={procesando}
        titulo="Anular abono"
        mensaje={
          filaAnular ? (
            <>
              <Typography component="p" sx={{ mb: 1 }}>
                {`Se anulará el abono #${filaAnular.id} por ${formatearMonto(filaAnular.totalRecibido)}. El plan de pagos vuelve al estado anterior a este abono.`}
              </Typography>
              <Typography component="p" sx={{ mb: 1 }}>
                Solo se puede anular el último abono del crédito: si hay uno más reciente,
                primero hay que anularlo.
              </Typography>
              <Typography component="p" sx={{ color: "text.secondary" }}>
                Esta acción no se puede deshacer.
              </Typography>
            </>
          ) : null
        }
        textoConfirmar="Anular abono"
        onConfirmar={confirmar}
        onCancelar={cerrarConfirmacion}
      />

      <AddAbono
        abierto={addQuoteModal}
        cliente={clientName}
        setShowModal={setAddQuoteModal}
        id={id}
        localReload={localReload}
        setLocalReload={recargarAbonos}
        isNominal={isNominal}
      />
    </div>
  );
};

export default CreditAbono;
